import { useEffect, useRef, useState } from "react";
import socketIOClient from "socket.io-client";
import { useDispatch } from "react-redux";
import { setRoomInfo } from "../../redux/room/room.actions";
import httpClient from "../api/http-client";
import { getTenantSlug } from "../../utils/tenant";
import { mesclarDadosDaSala } from "../../utils/dadosDaSala";

const NEW_CHAT_MESSAGE_EVENT = "NEW_CHAT_MESSAGE_EVENT";
const MESSAGE_READ_EVENT = "MESSAGE_READ_EVENT";
const MESSAGE_DELIVERED_EVENT = "MESSAGE_DELIVERED_EVENT";
const SENDING_MESSAGE_EVENT = "SENDING_MESSAGE_EVENT";
const SOCKET_SERVER_URL = process.env.REACT_APP_WS_URL || 'http://localhost:3001';
const NEW_FILE_EVENT = "NEW_FILE_EVENT";
const NEW_CLIENT_CONNECTED = "NEW_CLIENT_CONNECTED";

const useChat = (roomId) => {
  const [messages, setMessages] = useState([]);
  const [roomInfo, setRoomInfoLocal] = useState(JSON.parse(localStorage.getItem('roomInfo')) || {})
  const [receiveMessage, setReceiveMessage] = useState(false);
  const socketRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    // Fetching initial messages
    const fetchMessages = async () => {
      try {
        console.log('Fetching messages for roomId:', roomId);
        console.log('API URL:', process.env.REACT_APP_API_URL);

        const response = await httpClient.get(`/messages/${roomId}`);
        console.log('Messages fetched:', response.data);
        
        // Extrair o array de mensagens do response (pode ser paginado ou direto)
        const messagesArray = response.data && response.data.data ? response.data.data : response.data;
        
        if (Array.isArray(messagesArray)) {
          // Ordenar mensagens por data de criação (mais antigas primeiro)
          const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          console.log('Sorted messages:', sortedMessages);
          
          const lastMessage = sortedMessages.find(message => message.type === 1);
          if (lastMessage && lastMessage.user) {
            dispatch(setRoomInfo({ CRM_id: lastMessage.user.CRM_id }));
          }
          setMessages(sortedMessages);
          confirmDelivery(sortedMessages);
        } else {
          console.warn('Response data is not an array:', response.data);
          setMessages([]);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        setMessages([]);
      }
    };

    // Confirma ENTREGA (estilo WhatsApp) das mensagens recebidas do OPERADOR
    // (type 1) que ainda não foram entregues nem lidas, para que o operador veja
    // o status "entregue" (dois tiques cinza).
    const confirmDelivery = (msgs) => {
      if (!socketRef.current) return;
      (msgs || []).forEach((m) => {
        if (m && m.type === 1 && !m.read_at && (!m.meta || (m.meta.ack ?? 0) < 2)) {
          socketRef.current.emit(MESSAGE_DELIVERED_EVENT, { messageId: m.id, senderId: socketRef.current.id });
        }
      });
    };

    // Socket event handlers
    const handleConnect = () => {
      console.log('Socket connected, fetching messages...');
      fetchMessages();
    };

    // Fetch messages immediately when component mounts or roomId changes
    if (roomId && roomId !== 'undefined' && roomId !== 'null' && roomId !== '') {
      console.log('Initial fetch for roomId:', roomId);
      // Adiciona um pequeno delay para garantir que o componente esteja totalmente montado
      setTimeout(() => {
        fetchMessages();
      }, 100);
    } else {
      console.warn('Invalid roomId, not fetching messages:', roomId);
    }

    const handleNewChatMessage = (messages, data) => {
      console.log('handleNewChatMessage received:', messages, data);
      // Verificar se messages é um array ou se precisa extrair do data
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      
      if (Array.isArray(messagesArray) && messagesArray.length > 0) {
        // Ordenar mensagens por data de criação (mais antigas primeiro)
        const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        const lastMessage = sortedMessages.length > 0 ? sortedMessages[sortedMessages.length - 1] : null;
        const movideskId = lastMessage && lastMessage.user ? lastMessage.user.CRM_id : null;
        /*
         * MESCLADO com o que já se sabia, e não substituído.
         *
         * O evento interno do servidor (encerramento por inatividade, e a
         * despedida do Painel) vai com um argumento só, então `data` é
         * `undefined` -- e substituir apagaria `closing_message` no instante
         * em que a tela precisa dele para reconhecer o encerramento. A
         * mensagem que o próprio cliente manda tem o mesmo efeito, por outro
         * caminho. Ver `utils/dadosDaSala`.
         *
         * O valor anterior vem do `localStorage` de propósito: este efeito só
         * roda quando a sala troca, então o `roomInfo` do estado fechado aqui
         * dentro é o do primeiro render. O `localStorage` é a mesma fonte de
         * onde o estado nasce.
         */
        const guardado = (() => {
          try { return JSON.parse(localStorage.getItem('roomInfo') || '{}'); } catch { return {}; }
        })();
        const updatedData = mesclarDadosDaSala(guardado, data, movideskId);
        localStorage.setItem('roomInfo', JSON.stringify(updatedData));
        dispatch(setRoomInfo(updatedData));
        setRoomInfoLocal(updatedData);

        setMessages(sortedMessages);
        confirmDelivery(sortedMessages);
      } else {
        console.warn('handleNewChatMessage: dados não são um array, buscando mensagens novamente');
        fetchMessages();
      }
    };

    const handleNewFileEvent = (data) => {
      console.log('handleNewFileEvent received:', data);
      // Verificar se data é um array ou se precisa extrair do data
      const messagesArray = Array.isArray(data) ? data : (data && data.data ? data.data : (data && data.messages ? data.messages : []));
      
      if (Array.isArray(messagesArray) && messagesArray.length > 0) {
        // Ordenar mensagens por data de criação (mais antigas primeiro)
        const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(sortedMessages);
        confirmDelivery(sortedMessages);
      } else {
        // Se não for um array, busca as mensagens novamente
        console.warn('handleNewFileEvent: dados recebidos não são um array válido, buscando mensagens novamente:', data);
        fetchMessages();
      }
    };

    const handleSendingMessageEvent = (data) => {
      if (data.type === 1) {
        setReceiveMessage(true);
        setReceiveMessage(false);
      }
    };

    // Creating WebSocket connection
    console.log('Connecting to WebSocket:', SOCKET_SERVER_URL, 'with roomId:', roomId);
    // tenant_slug vai junto na query do handshake por consistência com as
    // chamadas HTTP (o servidor de WebSocket, fora deste repo, hoje resolve
    // a sala só pelo roomId -- isolado por tenant no banco -- mas manda o
    // slug também para o dia em que o servidor de socket quiser validar).
    socketRef.current = socketIOClient(SOCKET_SERVER_URL, { query: { roomId, tenant_slug: getTenantSlug() } });

    // Registering event listeners
    socketRef.current.on('connect', handleConnect);
    socketRef.current.on(NEW_CHAT_MESSAGE_EVENT, handleNewChatMessage);
    socketRef.current.on(MESSAGE_READ_EVENT, (messages) => {
      console.log('MESSAGE_READ_EVENT received:', messages);
      // Verificar se messages é um array ou se precisa extrair do data
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      
      if (Array.isArray(messagesArray) && messagesArray.length > 0) {
        // Ordenar mensagens por data de criação (mais antigas primeiro)
        const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(sortedMessages);
      } else {
        console.warn('MESSAGE_READ_EVENT: dados não são um array, buscando mensagens novamente');
        fetchMessages();
      }
    });
    socketRef.current.on(MESSAGE_DELIVERED_EVENT, (messages) => {
      // Atualiza os status de entrega (dois tiques cinza) recebidos do servidor.
      const messagesArray = Array.isArray(messages) ? messages : (messages && messages.data ? messages.data : []);
      if (Array.isArray(messagesArray) && messagesArray.length > 0) {
        const sortedMessages = messagesArray.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(sortedMessages);
      }
    });
    socketRef.current.on(NEW_FILE_EVENT, handleNewFileEvent);
    socketRef.current.on(SENDING_MESSAGE_EVENT, handleSendingMessageEvent);

    // Cleanup on component unmount
    return () => {
      socketRef.current.disconnect();
    };
  }, [roomId]);

  const sendMessage = (messageBody, id, type) => {
    // Message data construction
    const messageData = {
      body: messageBody,
      senderId: socketRef.current.id,
      roomId: roomId === undefined ? id : roomId,
      type: type,
      operatorId: roomInfo ? roomInfo.operatorId : null,
      name: roomInfo ? roomInfo.name : null,
      picture: roomInfo ? roomInfo.picture : null
    };

    // Emitting new chat message event
    socketRef.current.emit(NEW_CHAT_MESSAGE_EVENT, messageData);
  };

  const newRoom = (userData = null) => {
    const data = userData ? { name: userData.name, code: userData.code } : {};
    socketRef.current.emit(NEW_CLIENT_CONNECTED, data);
  }

  const sendFile = () => {
    socketRef.current.emit(NEW_FILE_EVENT, {});
  };

  const readMessage = (id) => {
    socketRef.current.emit(MESSAGE_READ_EVENT, { messageId: id, senderId: socketRef.current.id });
  };

  const sendingMessageEvent = () => {
    socketRef.current.emit(SENDING_MESSAGE_EVENT, { type: 0 });
  };

  return {
    messages,
    sendMessage,
    readMessage,
    sendingMessageEvent,
    receiveMessage,
    sendFile,
    roomInfo,
    newRoom
  };
};

export default useChat;
